import {some} from 'lodash';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Icon} from 'semantic-ui-react';
import {CodeEditorControl} from 'apstra-ui-common';

type PlainQueryInputProps = {
  filters: string;
  schema: any[];
  onChange: (filters: string) => void;
  applyOnChange?: boolean;
  highlightPropertyColumn?: (property: string | null) => void;
  disabled?: boolean;
  'aria-label'?: string;
};

const PlainQueryInput: FC<PlainQueryInputProps> = ({
  schema, filters: filtersFromProps, onChange: onChangeFromProps,
  applyOnChange, highlightPropertyColumn, disabled, 'aria-label': ariaLabel
}) => {
  const [filters, setFilters] = useState(filtersFromProps);

  useEffect(() => {
    setFilters(filtersFromProps);
  }, [filtersFromProps]);

  const onChange = useCallback((newFilters) => {
    setFilters(newFilters);
    if (applyOnChange) onChangeFromProps(newFilters);
  }, [onChangeFromProps, applyOnChange]);

  const commands = useMemo(() => [{
    name: 'submit',
    bindKey: 'Enter',
    // getting value from editor instance directly to avoid dependency on filters state
    exec: (editor) => onChangeFromProps(editor.getValue()),
  }], [onChangeFromProps]);

  const completerParams = useMemo(() => ({
    schema,
    excludeOperators: ['is', 'is not'],
  }), [schema]);

  const onCursorChange = useCallback(({session, cursor, isSelectionEmpty}) => {
    if (!highlightPropertyColumn) return;
    if (!session || !isSelectionEmpty) {
      highlightPropertyColumn(null);
      return;
    }
    const {getCurrentPropertyName} = session.getMode();
    if (getCurrentPropertyName) {
      const propertyName = getCurrentPropertyName(session, cursor);
      highlightPropertyColumn(some(schema, ({name}) => name === propertyName) ? propertyName : null);
    }
  }, [schema, highlightPropertyColumn]);

  return (
    <CodeEditorControl
      enableSearch={false}
      mode='matcher-filter-string'
      onChange={onChange}
      onCursorChange={onCursorChange}
      value={filters}
      disabled={disabled}
      completerParams={completerParams}
      commands={commands}
      actions={(applyOnChange || disabled) ? [] : [
        <Icon
          key='search'
          name='search'
          className='link'
          onClick={() => onChangeFromProps(filters)}
          aria-label='Apply'
        />
      ]}
      validate
      enableCompletion
      aria-label={ariaLabel}
    />
  );
};

export default PlainQueryInput;
